(function () {
  'use strict';

  class DeveloplyCart {
    constructor() {
      this.drawer    = document.getElementById('developly-cart');
      if (!this.drawer) return;

      this.overlay       = document.getElementById('developly-cart-overlay');
      this.body          = document.getElementById('dev-cart-body');
      this.emptyEl       = document.getElementById('dev-cart-empty');
      this.filledEl      = document.getElementById('dev-cart-filled');
      this.itemsEl       = document.getElementById('dev-cart-items');
      this.loaderEl      = document.getElementById('dev-cart-loader');
      this.shippingMsg   = document.getElementById('dev-cart-shipping-msg');
      this.progressFill  = document.getElementById('dev-cart-progress-fill');
      this.progressIcon  = document.getElementById('dev-cart-progress-icon');
      this.checkoutPrice = document.getElementById('dev-checkout-pricing');
      this.promoInput    = document.getElementById('dev-promo-input');
      this.promoMsg      = document.getElementById('dev-promo-msg');
      this.insuranceChk  = document.getElementById('dev-insurance-chk');

      this.cart               = null;
      this.isOpen             = false;
      this.isUpdating         = false;
      this.lastAppliedCode    = null; /* shown in fallback when API doesn't return code list */
      this._compareMap        = new Map();  /* variant_id -> compare_at_price (cents) */
      this._productCache      = new Map();  /* handle -> products.json payload */
      /* product_id -> titre d'affichage (2026-10-09). Table rendue par Liquid dans
         sections/developly-cart.liquid (#gh-titres-affichage), completee a la demande
         par les attributs data-gh-titre de la page (cartes, suggestions, bloc de la fiche). */
      this._titres = new Map();
      try {
        const brut = document.getElementById('gh-titres-affichage');
        const table = brut ? JSON.parse(brut.textContent) : {};
        Object.keys(table).forEach((id) => { if (table[id]) this._titres.set(String(id), String(table[id])); });
      } catch (e) { /* table illisible : repli sur _titreCourt() */ }
      this.freeShippingCents  = parseInt(this.drawer.dataset.freeShipping || '6900', 10);
      /* Pays avec point relais : 2e palier (domicile). 0 = un seul palier. */
      this.freeShippingHomeCents = parseInt(this.drawer.dataset.freeShippingHome || '0', 10);
      this.insuranceVariantId = this.drawer.dataset.insuranceVariant || null;

      /* Money format — use Shopify's shop.money_format string, passed via data attribute.
         Falls back to a generic placeholder if somehow missing. */
      this.moneyFormat = this.drawer.dataset.moneyFormat || '{{amount}}';

      /* i18n strings — all read from data-i18n-* attributes set by Liquid */
      this.i18n = {
        shippingFree:      this.drawer.dataset.i18nShippingFree      || 'Free delivery!',
        shippingRemaining: this.drawer.dataset.i18nShippingRemaining || 'Only __AMOUNT__ left for free delivery!',
        shippingRemainingRelay: this.drawer.dataset.i18nShippingRemainingRelay || '',
        shippingFreeRelay:      this.drawer.dataset.i18nShippingFreeRelay      || '',
        remove:            this.drawer.dataset.i18nRemove            || 'Remove',
        decrease:          this.drawer.dataset.i18nDecrease          || '-',
        increase:          this.drawer.dataset.i18nIncrease          || '+',
        quantityLabel:     this.drawer.dataset.i18nQuantityLabel     || 'Quantity for __TITLE__',
        crossAdd:          this.drawer.dataset.i18nCrossAdd          || 'Add',
        crossAdded:        this.drawer.dataset.i18nCrossAdded        || 'Added ✓',
        promoEmpty:        this.drawer.dataset.i18nPromoEmpty        || 'Please enter a promo code.',
        promoSuccess:      this.drawer.dataset.i18nPromoSuccess      || 'Promo code applied.',
        promoError:        this.drawer.dataset.i18nPromoError         || 'Invalid or expired code.',
        promoAppliedLabel:   this.drawer.dataset.i18nPromoAppliedLabel   || 'Applied code(s)',
        promoDiscountApplied: this.drawer.dataset.i18nPromoDiscountApplied || 'Discount applied',
        promoRemove:         this.drawer.dataset.i18nPromoRemove          || 'Remove',
      };

      /* Cadeaux par paliers (2026-10-11) : null tant que l'interrupteur de la section est eteint. */
      this.gifts = this._readGifts();

      this._init();
    }

    /* ══════════════════════════════════════════
       INIT
    ══════════════════════════════════════════ */
    async _init() {
      this._bindEvents();
      await this._fetchCart();
      this._giftBoundaries();
      this._render();
    }

    /* ══════════════════════════════════════════
       EVENTS
    ══════════════════════════════════════════ */
    _bindEvents() {
      /* Open triggers — add data-dev-cart-open to any element */
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-dev-cart-open]')) {
          e.preventDefault();
          this.open();
        }
      });

      /* Custom events for external scripts */
      document.addEventListener('developly:cart:open', () => this.open());
      document.addEventListener('developly:cart:refresh', async () => {
        await this._fetchCart();
        this._render();
      });

      /* Udesly (Webflow) AJAX add-to-cart dispatches on its event bus — there is no
         commerce-cart-wrapper in the header anymore, so nothing else listened.
         Bridge so collection cards + product forms refresh this drawer and open it. */
      this._bindUdeslyBridge();

      /* Close */
      document.getElementById('dev-cart-close')?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) this.close();
      });

      /* Quantity + remove — event delegation */
      this.itemsEl?.addEventListener('click', (e) => {
        const dec = e.target.closest('[data-dev-dec]');
        const inc = e.target.closest('[data-dev-inc]');
        const del = e.target.closest('[data-dev-del]');
        if (dec && !this.isUpdating) this._updateQty(dec.dataset.devDec, -1);
        if (inc && !this.isUpdating) this._updateQty(inc.dataset.devInc, +1);
        if (del && !this.isUpdating) {
          /* cadeau retire par le client : on s'en souvient pour ne pas le remettre */
          if (del.hasAttribute('data-dev-gift-del')) {
            this._giftDecline(this.cart?.items.find((i) => i.key === del.dataset.devDel));
          }
          this._removeItem(del.dataset.devDel);
        }
      });

      /* Cadeaux par paliers : on ne part au paiement qu'une fois les cadeaux a jour
         (bouton du tiroir, et bouton de la page /cart). Sans synchronisation en cours,
         ces deux ecouteurs ne font rien. */
      document.getElementById('dev-checkout-cta')?.addEventListener('click', (e) => this._giftLeave(e));
      document.addEventListener('submit', (e) => {
        if (e.target && e.target.closest && e.target.closest('.page-cart__form')) this._giftLeave(e);
      });

      /* Upsell form */
      document.getElementById('dev-cart-upsell-form')?.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (this.isUpdating) return;
        const form      = e.currentTarget;
        const select    = form.querySelector('select[name="id"]');
        const hidden    = form.querySelector('input[name="id"]');
        const variantId = parseInt((select ? select.value : hidden?.value) || '0', 10);
        if (!variantId) return;
        const btn = form.querySelector('.dev-cart__upsell-btn');
        btn?.classList.add('is-loading');
        await this._addToCart(variantId, 1);
        this._render();
        btn?.classList.remove('is-loading');
      });

      /* Upsell variant change → update displayed price */
      document.getElementById('dev-upsell-variant')?.addEventListener('change', (e) => {
        const priceCents = parseInt(e.target.options[e.target.selectedIndex].dataset.price || '0', 10);
        const priceEl    = document.getElementById('dev-upsell-price');
        if (priceEl && priceCents) priceEl.textContent = this._money(priceCents);
      });

      /* Promo code */
      document.getElementById('dev-promo-btn')?.addEventListener('click', () => this._applyPromo());
      this.promoInput?.addEventListener('keydown', (e) => { if (e.key === 'Enter') this._applyPromo(); });

      /* Insurance toggle */
      this.insuranceChk?.addEventListener('change', async (e) => {
        if (!this.insuranceVariantId || this.isUpdating) {
          e.target.checked = !e.target.checked;
          return;
        }
        await this._toggleInsurance(e.target.checked);
      });

      /* Cross-sell add buttons */
      document.addEventListener('click', async (e) => {
        const btn = e.target.closest('.dev-cart__cross-add[data-variant-id]');
        if (!btn || this.isUpdating) return;
        const variantId = parseInt(btn.dataset.variantId, 10);
        if (!variantId) return;
        btn.classList.add('is-loading');
        await this._addToCart(variantId, 1);
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        const orig = btn.textContent;
        btn.textContent = this.i18n.crossAdded;
        this._render();
        setTimeout(() => { btn.classList.remove('is-added'); btn.textContent = orig; }, 2500);
      });

      /* Cross-sell scroll navigation */
      document.getElementById('dev-cross-prev')?.addEventListener('click', () => {
        document.getElementById('dev-cross-scroll')?.scrollBy({ left: -273, behavior: 'smooth' });
      });
      document.getElementById('dev-cross-next')?.addEventListener('click', () => {
        document.getElementById('dev-cross-scroll')?.scrollBy({ left: 273, behavior: 'smooth' });
      });

      /* Applied discount codes — remove one or all */
      document.getElementById('dev-cart-applied-codes')?.addEventListener('click', async (e) => {
        const removeBtn = e.target.closest('.dev-cart__code-tag-x[data-dev-remove-code]');
        const removeAll  = e.target.closest('.dev-cart__code-tag-x[data-dev-remove-all-codes]');
        if (this.isUpdating) return;
        if (removeBtn) {
          const codeToRemove = removeBtn.dataset.devRemoveCode;
          const codes        = this._getAppliedDiscountCodes();
          const next         = codes.filter((c) => c !== codeToRemove);
          await this._updateCartDiscount(next.length ? next.join(',') : '');
          return;
        }
        if (removeAll) {
          this.lastAppliedCode = null;
          await this._updateCartDiscount('');
        }
      });
    }

    _bindUdeslyBridge() {
      const U = window.Udesly;
      if (!U || typeof U.on !== 'function') return;

      const syncFromServer = async () => {
        await this._fetchCart();
        this._render();
      };

      U.on('product-added', (shouldOpen) => {
        void (async () => {
          await syncFromServer();
          /* Default Udesly behavior: open cart when detail is true/undefined; skip when false */
          if (shouldOpen !== false) this.open();
        })();
      });

      U.on('cart-updated', () => {
        void syncFromServer();
      });

      U.on('cart-should-be-updated', () => {
        void syncFromServer();
      });
    }

    /* ══════════════════════════════════════════
       SHOPIFY AJAX CART API
    ══════════════════════════════════════════ */
    async _fetchCart() {
      try {
        const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!r.ok) throw new Error(r.statusText);
        if (this.gifts) this._giftClock(r);
        this.cart = await r.json();
      } catch (err) {
        /* Cart fetch failed; leave existing state. No console in production. */
      }
    }

    async _changeCart(key, quantity) {
      const r = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!r.ok) throw new Error(await r.text());
      this.cart = await r.json();
    }

    async _addToCart(variantId, quantity, properties) {
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity, properties: properties || {} }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.description || "Erreur lors de l'ajout au panier");
      }
      await this._fetchCart();
    }

    /* ══════════════════════════════════════════
       ACTIONS
    ══════════════════════════════════════════ */
    async _updateQty(key, delta) {
      const item = this.cart?.items.find((i) => i.key === key);
      if (!item) return;
      const newQty = Math.max(0, item.quantity + delta);
      this.isUpdating = true;
      try {
        await this._changeCart(key, newQty);
        this._syncInsuranceToggle();
        this._render();
      } catch (err) {
        /* Update failed; keep current state */
      } finally {
        this.isUpdating = false;
      }
    }

    async _removeItem(key) {
      this.isUpdating = true;
      try {
        await this._changeCart(key, 0);
        this._syncInsuranceToggle();
        this._render();
      } catch (err) {
        /* Remove failed; keep current state */
      } finally {
        this.isUpdating = false;
      }
    }

    async _toggleInsurance(add) {
      if (!this.insuranceVariantId) return;
      this.isUpdating = true;
      try {
        if (add) {
          await this._addToCart(parseInt(this.insuranceVariantId, 10), 1);
        } else {
          const item = this.cart?.items.find(
            (i) => String(i.variant_id) === String(this.insuranceVariantId)
          );
          if (item) await this._changeCart(item.key, 0);
        }
        this._render();
      } catch (err) {
        /* Toggle failed; sync checkbox state */
      } finally {
        this.isUpdating = false;
        this._syncInsuranceToggle();
      }
    }

    async _updateCartDiscount(discountValue) {
      this.isUpdating = true;
      try {
        const r = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: discountValue }),
        });
        if (!r.ok) throw new Error(await r.text());
        this.cart = await r.json();
        this._render();
      } catch (err) {
        /* Discount update failed; keep current state */
      } finally {
        this.isUpdating = false;
      }
    }

    _getAppliedDiscountCodes() {
      const apps = this.cart?.cart_level_discount_applications;
      if (Array.isArray(apps) && apps.length > 0) {
        return apps.map((a) => (a.title != null ? String(a.title).trim() : '')).filter(Boolean);
      }
      if (typeof this.cart?.total_discount === 'number' && this.cart.total_discount > 0) {
        return [];
      }
      return [];
    }

    _renderAppliedCodes() {
      const container = document.getElementById('dev-cart-applied-codes');
      if (!container) return;
      const codes = this._getAppliedDiscountCodes();
      const hasDiscount = codes.length > 0 ||
        (typeof this.cart?.total_discount === 'number' && this.cart.total_discount > 0);
      if (!hasDiscount) {
        this.lastAppliedCode = null;
        container.classList.remove('is-visible');
        container.innerHTML = '';
        return;
      }
      container.classList.add('is-visible');
      const remove = this._esc(this.i18n.promoRemove);
      if (codes.length > 0) {
        this.lastAppliedCode = null;
        container.innerHTML =
          '<div class="dev-cart__code-tags">' +
          codes.map((code) =>
            '<span class="dev-cart__code-tag">' +
            '<span class="dev-cart__code-tag-name">' + this._esc(code) + '</span>' +
            '<button type="button" class="dev-cart__code-tag-x" data-dev-remove-code="' + this._esc(code) + '" aria-label="' + remove + ' ' + this._esc(code) + '">&times;</button>' +
            '</span>'
          ).join('') +
          '</div>';
      } else {
        const displayCode = this.lastAppliedCode ? this._esc(this.lastAppliedCode) : null;
        const discountLabel = this._esc(this.i18n.promoDiscountApplied);
        container.innerHTML =
          '<div class="dev-cart__code-tags">' +
          '<span class="dev-cart__code-tag">' +
          (displayCode ? '<span class="dev-cart__code-tag-name">' + displayCode + '</span>' : '<span class="dev-cart__code-tag-fallback">' + discountLabel + '</span>') +
          '<button type="button" class="dev-cart__code-tag-x" data-dev-remove-all-codes aria-label="' + remove + '">&times;</button>' +
          '</span>' +
          '</div>';
      }
    }

    async _applyPromo() {
      const code = this.promoInput?.value.trim();
      if (!code) {
        this._setPromoMsg(this.i18n.promoEmpty, 'err');
        return;
      }
      const btn = document.getElementById('dev-promo-btn');
      if (btn) {
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
      }
      this._setPromoMsg('', '');
      const prevDiscount = (this.cart && typeof this.cart.total_discount === 'number') ? this.cart.total_discount : 0;
      const applyUrl     = '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(window.location.pathname + window.location.search);
      try {
        const r = await fetch(applyUrl, { method: 'GET', credentials: 'same-origin', redirect: 'follow' });
        if (!r.ok) throw new Error('Discount request failed');
        await this._fetchCart();
        const hasNewDiscount = typeof this.cart?.total_discount === 'number' && this.cart.total_discount > prevDiscount;
        if (hasNewDiscount) {
          this.lastAppliedCode = code;
          this._setDiscountCookie(code);
          this._setPromoMsg(this.i18n.promoSuccess, 'ok');
          if (this.promoInput) this.promoInput.value = '';
          this._render();
        } else {
          this._setPromoMsg(this.i18n.promoError, 'err');
        }
      } catch (err) {
        this._setPromoMsg(this.i18n.promoError, 'err');
      } finally {
        if (btn) {
          btn.disabled = false;
          btn.removeAttribute('aria-busy');
        }
      }
    }

    /* ══════════════════════════════════════════
       OPEN / CLOSE
    ══════════════════════════════════════════ */
    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this._focusPrevious = document.activeElement;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.overlay?.classList.add('is-open');
      this.overlay?.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      if (this.gifts) this._giftOpening = true;   /* ajouts faits hors du panier : on relit avant de laisser payer */
      /* 2026-10-28 (lot 1, B13) : juste apres un ajout, le tiroir affichait encore l'etat d'avant (« Votre panier
         est vide ») pendant la relecture du panier. L'etat vide est masque jusqu'a la fin de ce rendu. */
      const videAvant = !!(this.emptyEl && this.emptyEl.classList.contains('is-visible'));
      if (videAvant) this.emptyEl.style.visibility = 'hidden';
      this._fetchCart().then(() => this._render()).finally(() => {
        if (videAvant && this.emptyEl) this.emptyEl.style.visibility = '';
      });
      this._bindFocusTrap();
      requestAnimationFrame(() => {
        const first = this._getFocusables()[0];
        if (first) first.focus();
      });
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this._unbindFocusTrap();
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.overlay?.classList.remove('is-open');
      this.overlay?.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (this._focusPrevious && typeof this._focusPrevious.focus === 'function') {
        this._focusPrevious.focus();
      }
      this._focusPrevious = null;
    }

    _getFocusables() {
      const sel = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      return Array.prototype.slice.call(this.drawer.querySelectorAll(sel));
    }

    _bindFocusTrap() {
      this._focusTrapHandler = (e) => {
        if (e.key !== 'Tab') return;
        const focusables = this._getFocusables();
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      };
      this.drawer.addEventListener('keydown', this._focusTrapHandler);
    }

    _unbindFocusTrap() {
      if (this._focusTrapHandler) {
        this.drawer.removeEventListener('keydown', this._focusTrapHandler);
        this._focusTrapHandler = null;
      }
    }

    /* ══════════════════════════════════════════
       RENDER
    ══════════════════════════════════════════ */
    async _render() {
      if (!this.cart) return;
      this._queueGiftSync();
      const displayItems = this._getDisplayItems();
      const isEmpty = displayItems.length === 0;

      /* Hydrate compare_at_prices pour les items du cart (absent de /cart.js) */
      if (!isEmpty) await this._hydrateCompareAt(displayItems);

      if (this.emptyEl) {
        this.emptyEl.style.display = isEmpty ? 'flex' : 'none';
        this.emptyEl.classList.toggle('is-visible', isEmpty);
      }
      if (this.filledEl) {
        this.filledEl.style.display = isEmpty ? 'none' : 'flex';
        this.filledEl.classList.toggle('is-visible', !isEmpty);
      }

      if (!isEmpty) {
        this._renderShipping(displayItems);
        this._renderItems(displayItems);
        this._renderTotals(displayItems);
        this._syncInsuranceToggle();
        this._renderAppliedCodes();
      } else {
        this._renderAppliedCodes();
      }

      /* Update cart count badges sitewide */
      const totalQty = this._getDisplayItems().reduce((s, i) => s + i.quantity, 0);
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = totalQty;
        el.style.display = totalQty > 0 ? 'inline-block' : 'none';
      });
      this._giftHeaderCount();

      /* Checkout link: pass discount in URL so checkout receives the code */
      const checkoutLink = document.getElementById('dev-checkout-cta');
      if (checkoutLink) {
        const codes = this._getAppliedDiscountCodes();
        const code = codes.length > 0 ? codes[0] : (this.lastAppliedCode || '');
        /* Adresse avec le prefixe de langue / marche (/it-it/checkout…), posee par Liquid. */
        const checkoutBase = checkoutLink.dataset.checkoutUrl || '/checkout';
        if (code) {
          this._setDiscountCookie(code);
          checkoutLink.href = checkoutBase + '?discount=' + encodeURIComponent(code);
        } else {
          this._clearDiscountCookie();
          checkoutLink.href = checkoutBase;
        }
      }

      this._updateCrossSell();
    }

    /* Cross-sell : un produit recommandé déjà au panier est remplacé par son
       fallback (même collection, rendu hidden côté Liquid). Si ni le produit
       ni aucun de ses fallbacks ne sont montrables, le slot est masqué ; le
       bloc entier disparaît quand plus aucun slot n'est visible. */
    _updateCrossSell() {
      const scroll = document.getElementById('dev-cross-scroll');
      if (!scroll) return;
      const inCart = new Set((this.cart?.items || []).map((i) => String(i.product_id)));
      const shownIds = new Set();
      let anyVisible = false;

      scroll.querySelectorAll('.dev-cart__cross-item[data-cross-main]').forEach((main) => {
        const mainId = main.dataset.productId;
        const fallbacks = scroll.querySelectorAll(
          '.dev-cart__cross-item[data-fallback-for="' + mainId + '"]'
        );
        const candidates = [main, ...fallbacks];
        let shown = null;
        candidates.forEach((el) => {
          const pid = el.dataset.productId;
          if (!shown && !inCart.has(pid) && !shownIds.has(pid)) shown = el;
        });
        candidates.forEach((el) => { el.hidden = el !== shown; });
        if (shown) {
          shownIds.add(shown.dataset.productId);
          anyVisible = true;
        }
      });

      const crossBlock = scroll.closest('.dev-cart__cross');
      if (crossBlock) crossBlock.hidden = !anyVisible;
    }

    _getDisplayItems() {
      if (!this.cart?.items) return [];
      const base = !this.insuranceVariantId
        ? this.cart.items
        : this.cart.items.filter((i) => String(i.variant_id) !== String(this.insuranceVariantId));
      if (!base.some((i) => this._isGift(i))) return base;   /* aucune ligne cadeau : la liste d'avant */
      /* Cadeaux en bas de liste, dans l'ordre des paliers ; un cadeau sur le point d'etre retire
         n'est deja plus affiche. */
      const plan = this._giftPlan();
      const rank = (i) => {
        const t = this.gifts ? this.gifts.tiers.find((x) => x.n === this._giftTierOf(i)) : null;
        return t ? t.cents : 0;
      };
      return base.filter((i) => !this._isGift(i)).concat(
        base.filter((i) => this._isGift(i) && plan.updates[i.key] !== 0).sort((a, b) => rank(a) - rank(b))
      );
    }

    _renderShipping(items) {
      if (!this.shippingMsg || !this.progressFill) return;
      if (this.gifts) {
        /* promo en cours : barre a paliers (livraison + cadeaux) ; sinon la barre d'avant */
        if (this._giftsActive()) { this._renderTiers(); return; }
        this._clearTiers();
      }
      /* 2026-10-28 (lot 1, B2) : livraison offerte comptee APRES les remises de commande, comme au paiement */
      const subtotal  = this._shipSubtotal();
      /* 2 paliers quand le pays a un point relais (relais puis domicile), sinon 1 seul. */
      const twoSteps  = this.freeShippingHomeCents > this.freeShippingCents
                        && !!this.i18n.shippingRemainingRelay && !!this.i18n.shippingFreeRelay;
      const lastCents = twoSteps ? this.freeShippingHomeCents : this.freeShippingCents;
      const pct       = Math.min(100, (subtotal / lastCents) * 100);

      this.progressFill.style.width = pct + '%';

      if (subtotal >= lastCents) {
        this.shippingMsg.innerHTML = '<em>' + this._esc(this.i18n.shippingFree) + '</em> 🎉';
        if (this.progressIcon) this.progressIcon.style.opacity = '0';
      } else {
        const relayReached = twoSteps && subtotal >= this.freeShippingCents;
        const template  = relayReached ? this.i18n.shippingFreeRelay
                        : (twoSteps ? this.i18n.shippingRemainingRelay : this.i18n.shippingRemaining);
        const remaining = (relayReached ? lastCents : this.freeShippingCents) - subtotal;
        const amountStr = this._money(remaining);
        const msg       = template.replace('__AMOUNT__', '<strong>' + amountStr + '</strong>');
        this.shippingMsg.innerHTML = '<em>' + msg + '</em>';
        if (this.progressIcon) this.progressIcon.style.opacity = '1';
      }
    }

    _renderItems(items) {
      if (!this.itemsEl) return;
      if (this.loaderEl) this.loaderEl.style.display = 'none';
      this.itemsEl.innerHTML = items.map((item) => this._itemHTML(item)).join('');
    }

    /* Titre d'affichage d'une ligne : table Liquid, sinon attribut data-gh-titre present dans
       la page, sinon la meme regle que snippets/gh-titre-affichage.liquid appliquee au titre
       complet (coupe avant " – " ou " | ", nom de marque repete retire). */
    _titreAffichage(item) {
      const id = String(item.product_id);
      if (this._titres.has(id)) return this._titres.get(id);
      let titre = '';
      const el = document.querySelector('[data-gh-titre][data-product-id="' + id.replace(/[^0-9]/g, '') + '"]');
      if (el) titre = (el.getAttribute('data-gh-titre') || '').trim();
      if (!titre) titre = this._titreCourt(item.product_title);
      this._titres.set(id, titre);
      return titre;
    }

    _titreCourt(titreComplet) {
      const complet = String(titreComplet ?? '').trim();
      let court = complet.split(' – ')[0].split(' | ')[0].trim();
      ['Golden CBD', 'Golden High'].forEach((marque) => {
        const sans = court.split(' ' + marque).join('').split(marque + ' ').join('').trim();
        if (sans) court = sans;
      });
      return court || complet;
    }

    _itemHTML(item) {
      if (this._isGift(item)) return this._giftItemHTML(item);
      const imgSrc = item.featured_image?.url
        ? this._imgSrc(item.featured_image.url, '176x176')
        : item.image
          ? this._imgSrc(item.image, '176x176')
          : '';

      const variantLabel =
        item.variant_title && item.variant_title !== 'Default Title'
          ? '<p class="dev-cart__item-variant">' + this._esc(item.variant_title) + '</p>'
          : '';

      const canDecrease   = item.quantity > 1;
      const qtyLabel      = this.i18n.quantityLabel.replace('__TITLE__', this._esc(item.product_title));
      const decreaseLabel = this.i18n.decrease;
      const increaseLabel = this.i18n.increase;
      const removeLabel   = this.i18n.remove;

      return /* html */ `
        <div class="dev-cart__item" data-key="${item.key}">
          <div class="dev-cart__item-img">
            ${imgSrc ? `<img src="${imgSrc}" alt="${this._esc(item.product_title)}" loading="lazy" width="88" height="88">` : ''}
          </div>
          <div class="dev-cart__item-mid">
            <p class="dev-cart__item-title" title="${this._esc(item.product_title)}">${this._esc(this._titreAffichage(item))}</p>
            ${variantLabel}
            <div class="dev-cart__item-qty" role="group" aria-label="${qtyLabel}">
              <button type="button" class="dev-cart__qty-btn" data-dev-dec="${item.key}" aria-label="${decreaseLabel}" ${canDecrease ? '' : 'disabled'}>
                <svg width="12" height="2" viewBox="0 0 12 2" fill="none" aria-hidden="true">
                  <rect x="0" y="0.25" width="12" height="1.5" rx="0.75" fill="white"/>
                </svg>
              </button>
              <span class="dev-cart__qty-val" aria-live="polite">${item.quantity}</span>
              <button type="button" class="dev-cart__qty-btn" data-dev-inc="${item.key}" aria-label="${increaseLabel}">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <rect x="0" y="5.25" width="12" height="1.5" rx="0.75" fill="white"/>
                  <rect x="5.25" y="0" width="1.5" height="12" rx="0.75" fill="white"/>
                </svg>
              </button>
            </div>
          </div>
          <div class="dev-cart__item-right">
            ${this._itemPriceHTML(item)}
            <button type="button" class="dev-cart__item-delete" data-dev-del="${item.key}" aria-label="${removeLabel} ${this._esc(item.product_title)}">
              ${removeLabel}
            </button>
          </div>
        </div>`;
    }

    _compareAtFor(item) {
      const fromCart = typeof item.compare_at_price === 'number' ? item.compare_at_price : null;
      const fromCache = this._compareMap.get(String(item.variant_id));
      const compareCents = fromCart != null ? fromCart : fromCache;
      if (typeof compareCents !== 'number' || compareCents <= item.price) return 0;
      return compareCents;
    }

    _itemPriceHTML(item) {
      const compareUnit = this._compareAtFor(item);
      if (!compareUnit) {
        return '<p class="dev-cart__item-price">' + this._money(item.line_price) + '</p>';
      }
      const compareLine = compareUnit * item.quantity;
      return (
        '<div class="dev-cart__item-prices">' +
          '<span class="dev-cart__item-compare">' + this._money(compareLine) + '</span>' +
          '<span class="dev-cart__item-price">'   + this._money(item.line_price) + '</span>' +
        '</div>'
      );
    }

    async _hydrateCompareAt(items) {
      const handles = [...new Set(items.map((i) => i.handle).filter(Boolean))];
      const missing = handles.filter((h) => !this._productCache.has(h));
      if (!missing.length) return;

      await Promise.all(missing.map(async (handle) => {
        try {
          const r = await fetch('/products/' + encodeURIComponent(handle) + '.js', { cache: 'no-store' });
          if (!r.ok) return;
          const p = await r.json();
          this._productCache.set(handle, p);
          (p.variants || []).forEach((v) => {
            if (typeof v.compare_at_price === 'number') {
              this._compareMap.set(String(v.id), v.compare_at_price);
            }
          });
        } catch (e) {
          /* silencieux — l'item affiche son prix sans compare si indispo */
        }
      }));
    }

    _renderTotals(items) {
      const totalPrice = typeof this.cart.total_price === 'number' ? this.cart.total_price : 0;
      const originalTotal = typeof this.cart.original_total_price === 'number' ? this.cart.original_total_price : totalPrice;
      const totalDiscount = typeof this.cart.total_discount === 'number' ? this.cart.total_discount : 0;

      // Prix "initial" = somme des compare_at_price * qty (ou price si pas de compare_at)
      const compareTotal = items.reduce((s, i) => {
        const compareUnit = this._compareAtFor(i);
        const unit = compareUnit || i.price;
        return s + unit * i.quantity;
      }, 0);

      // Prend le plus eleve entre compare_at total et original_total_price (discount codes).
      const refTotal = Math.max(originalTotal, compareTotal);
      const savings = Math.max(0, refTotal - totalPrice, totalDiscount);

      if (this.checkoutPrice) {
        if (savings > 0 && refTotal > totalPrice) {
          this.checkoutPrice.innerHTML =
            '<span class="dev-cart__compare">' + this._money(refTotal) + '</span>' +
            '<span class="dev-cart__total">'   + this._money(totalPrice)   + '</span>';
        } else {
          this.checkoutPrice.innerHTML =
            '<span class="dev-cart__total">' + this._money(totalPrice) + '</span>';
        }
      }
      /* 2026-10-28 (panier) : ligne discrete au-dessus du bouton : « Tu economises X » (prix barre du bouton - total,
         meme calcul). Le prix barre reste DANS le bouton (retour d'Alex). */
      const ecoEl = document.getElementById('dev-cart-savings');
      const ecoTpl = this.drawer.dataset.i18nSavings || '';
      if (ecoEl) {
        const ecoOk = !!ecoTpl && savings > 0 && refTotal > totalPrice;
        ecoEl.hidden = !ecoOk;
        ecoEl.innerHTML = ecoOk
          ? '<span>' + this._esc(ecoTpl).replace('__AMOUNT__', '<strong>' + this._money(refTotal - totalPrice) + '</strong>') + '</span>'
          : '';
      }
    }

    _syncInsuranceToggle() {
      if (!this.insuranceChk || !this.insuranceVariantId) return;
      const has = !!this.cart?.items.find(
        (i) => String(i.variant_id) === String(this.insuranceVariantId)
      );
      this.insuranceChk.checked = has;
    }

    /* ══════════════════════════════════════════
       CADEAUX PAR PALIERS (2026-10-11)

       Reglages : section du panier, bloc « Cadeaux par paliers » (interrupteur, dates en heure
       de Paris, 3 paliers). Liquid ne pose la table #gh-cadeaux-config que si l'interrupteur
       est allume ; sans elle this.gifts vaut null et rien de ce qui suit ne s'execute.

       Une ligne cadeau = une ligne du panier qui porte la propriete cachee _gh_cadeau
       (valeur : numero du palier). Un article achete normalement n'est donc jamais pris pour
       un cadeau, meme si c'est la meme variante.

       Le total qui compte = articles payants seulement (ni cadeaux, ni assurance colis).
       Le code ne suppose rien sur le prix : il affiche celui que renvoie /cart.js.
    ══════════════════════════════════════════ */
    _readGifts() {
      try {
        const el = document.getElementById('gh-cadeaux-config');
        if (!el) return null;
        const c = JSON.parse(el.textContent);
        const tiers = (c.paliers || []).map((t) => ({
          n: String(t.n),
          cents: Math.round(parseFloat(String(t.seuil == null ? '' : t.seuil).replace(',', '.')) * 100),
          /* identifiant : le dernier groupe de chiffres (accepte aussi gid://shopify/ProductVariant/123) */
          variant: (String(t.variante == null ? '' : t.variante).match(/\d{5,}/g) || []).pop() || '',
          label: String(t.libelle == null ? '' : t.libelle).trim(),
        })).filter((t) => t.cents > 0 && t.variant).sort((a, b) => a.cents - b.cents);
        if (!tiers.length) return null;
        const start = this._parisTime(c.debut);
        const end = this._parisTime(c.fin);
        if (start === false || end === false) {
          console.warn('[panier] cadeaux : date de début ou de fin illisible, promo inactive');
          return null;
        }
        /* Shopify echappe les textes traduits (&#39; pour l'apostrophe) : on les remet en clair,
           ils sont re-echappes a l'affichage. */
        const t = {};
        const plain = document.createElement('textarea');
        Object.keys(c.textes || {}).forEach((k) => {
          plain.innerHTML = String(c.textes[k] == null ? '' : c.textes[k]);
          t[k] = plain.value;
        });
        return { tiers, start, end, freeOnly: c.gratuit_seulement !== false, t };
      } catch (e) {
        return null;
      }
    }

    /* « 2026-10-11 00:00 » (ou « 11/10/2026 00:00 ») en heure de Paris -> instant.
       Vide -> null (pas de borne). Illisible -> false (la promo reste inactive). */
    _parisTime(txt) {
      const s = String(txt == null ? '' : txt).trim();
      if (!s) return null;
      let y, mo, d, h, mi;
      let m = s.match(/^(\d{4})\D(\d{1,2})\D(\d{1,2})(?:\D+(\d{1,2})\D(\d{2}))?$/);
      if (m) {
        y = +m[1]; mo = +m[2]; d = +m[3];
      } else {
        m = s.match(/^(\d{1,2})\D(\d{1,2})\D(\d{4})(?:\D+(\d{1,2})\D(\d{2}))?$/);
        if (!m) return false;
        d = +m[1]; mo = +m[2]; y = +m[3];
      }
      h = +(m[4] || 0); mi = +(m[5] || 0);
      if (mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59) return false;
      const wall = Date.UTC(y, mo - 1, d, h, mi);
      const offset = (t) => {
        try {
          const p = {};
          new Intl.DateTimeFormat('en-GB', {
            timeZone: 'Europe/Paris', hourCycle: 'h23',
            year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
          }).formatToParts(new Date(t)).forEach((x) => { p[x.type] = +x.value; });
          return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute) - t;
        } catch (e) {
          return (mo > 3 && mo < 11 ? 2 : 1) * 3600000;
        }
      };
      /* deux passes : exact aussi autour d'un changement d'heure */
      return wall - offset(wall - offset(wall));
    }

    /* Heure du serveur (en-tete Date de /cart.js) plutot que l'horloge de l'appareil. */
    _giftClock(r) {
      try {
        const d = Date.parse(r.headers.get('Date') || '');
        if (!isNaN(d)) this._giftSkew = Math.abs(d - Date.now()) > 120000 ? d - Date.now() : 0;
      } catch (e) { /* horloge de l'appareil */ }
    }

    _giftNow() {
      return Date.now() + (this._giftSkew || 0);
    }

    _giftsActive() {
      const g = this.gifts;
      if (!g) return false;
      const now = this._giftNow();
      return (g.start === null || now >= g.start) && (g.end === null || now < g.end);
    }

    /* Panier ouvert pendant que la promo commence ou finit : on redessine a l'heure dite. */
    _giftBoundaries() {
      if (!this.gifts) return;
      [this.gifts.start, this.gifts.end].forEach((t) => {
        if (t === null) return;
        const wait = t - this._giftNow();
        if (wait > 0 && wait < 6 * 3600000) setTimeout(() => this._render(), wait + 1500);
      });
    }

    _giftTierOf(item) {
      const v = item && item.properties ? item.properties._gh_cadeau : null;
      return v === undefined || v === null || v === '' ? null : String(v);
    }

    _isGift(item) {
      return this._giftTierOf(item) !== null;
    }

    _hasGiftLines() {
      return !!(this.cart && this.cart.items && this.cart.items.some((i) => this._isGift(i)));
    }

    _linePrice(item) {
      return typeof item.final_line_price === 'number' ? item.final_line_price : (item.line_price || 0);
    }

    /* Total des articles payants : sans les cadeaux, sans l'assurance colis. */
    _paidSubtotal() {
      if (!this.cart || !this.cart.items) return 0;
      return this.cart.items.reduce((s, i) => {
        if (this._isGift(i)) return s;
        if (this.insuranceVariantId && String(i.variant_id) === String(this.insuranceVariantId)) return s;
        return s + this._linePrice(i);
      }, 0);
    }

    /* Montant qui compte pour la LIVRAISON offerte (2026-10-28, lot 1, B2). Shopify compare les seuils des
       tarifs au montant APRES les remises de commande (code CIAO : 63,90 - 10 = 53,90 -> livraison payante).
       = total_price de /cart.js (apres toutes les remises) moins les lignes cadeau et l'assurance colis.
       Les paliers CADEAU restent sur _paidSubtotal() (final_line_price, avant les remises de commande) : c'est
       la base de la reduction automatique « Cadeau offert des 89 € » (Achetez X, montant 89 € dans la
       collection « base du palier », reduction de produit calculee avant les codes de commande). */
    _shipSubtotal() {
      if (!this.cart || !this.cart.items || typeof this.cart.total_price !== 'number') return this._paidSubtotal();
      const horsPalier = this.cart.items.reduce((s, i) => {
        const assurance = this.insuranceVariantId && String(i.variant_id) === String(this.insuranceVariantId);
        return this._isGift(i) || assurance ? s + this._linePrice(i) : s;
      }, 0);
      return Math.max(0, this.cart.total_price - horsPalier);
    }

    /* Memoire de session : paliers en echec (10 min) et cadeaux retires par le client. */
    _giftMemo(name, value) {
      try {
        if (value === undefined) return JSON.parse(sessionStorage.getItem(name) || '{}') || {};
        sessionStorage.setItem(name, JSON.stringify(value));
      } catch (e) { /* stockage indisponible : memoire de la page seulement */ }
      return value || {};
    }

    /* null (palier normal) | 'stock' (ajout refuse) | 'prix' (cadeau non gratuit) | 'refus' (retire par le client) */
    _giftState(t) {
      const key = t.n + ':' + t.variant;
      if (!this._giftKoMap) {
        this._giftKoMap = this._giftMemo('gh_cadeaux_ko');
        this._giftNoMap = this._giftMemo('gh_cadeaux_refus');
      }
      if (this._giftNoMap[key]) return 'refus';
      const ko = this._giftKoMap[key];
      if (ko && Date.now() - ko.t < 600000) return ko.r;
      return null;
    }

    _giftSetKo(t, reason) {
      this._giftState(t);
      this._giftKoMap[t.n + ':' + t.variant] = { r: reason, t: Date.now() };
      this._giftMemo('gh_cadeaux_ko', this._giftKoMap);
    }

    _giftDecline(item) {
      const n = this._giftTierOf(item);
      const t = this.gifts ? this.gifts.tiers.find((x) => x.n === n) : null;
      if (!t) return;
      this._giftState(t);
      this._giftNoMap[t.n + ':' + t.variant] = 1;
      this._giftMemo('gh_cadeaux_refus', this._giftNoMap);
    }

    /* Securite : un cadeau qui n'est pas a 0 (reduction absente ou bloquee par un code) est
       marque en echec ; le plan le retire au lieu de le laisser facturer. */
    _giftCheckPrices() {
      if (!this.gifts || !this.gifts.freeOnly || !this._giftsActive() || !this.cart) return;
      /* Sous son palier, un cadeau redevient payant de lui-meme (la reduction part) : ce n'est pas un
         echec, le plan retire la ligne. On ne marque que les cadeaux dont le palier est atteint. */
      const sub = this._paidSubtotal();
      this.cart.items.forEach((i) => {
        if (!this._isGift(i) || this._linePrice(i) <= 0) return;
        /* le client a aussi achete cette variante : Shopify peut avoir pose la remise sur l'autre ligne */
        const elsewhere = this.cart.items.some((o) => o !== i && !this._isGift(o)
          && String(o.variant_id) === String(i.variant_id)
          && typeof o.original_line_price === 'number'
          && o.original_line_price - this._linePrice(o) >= this._linePrice(i));
        if (elsewhere) return;
        const t = this.gifts.tiers.find((x) => x.n === this._giftTierOf(i) && x.variant === String(i.variant_id));
        if (t && sub >= t.cents && !this._giftState(t)) this._giftSetKo(t, 'prix');
      });
    }

    /* Ce qu'il faut changer pour que les lignes cadeau collent au total payant.
       updates : { cle de ligne: 0 (retirer) ou 1 (ramener a 1) } ; add : paliers a ajouter. */
    _giftPlan() {
      const plan = { updates: {}, add: [], sub: 0, active: false };
      if (!this.cart || !this.cart.items) return plan;
      plan.active = this._giftsActive();
      plan.sub = this._paidSubtotal();
      const wanted = plan.active
        ? this.gifts.tiers.filter((t) => plan.sub >= t.cents && !this._giftState(t))
        : [];
      const seen = {};
      this.cart.items.forEach((i) => {
        if (!this._isGift(i)) return;
        const t = wanted.find((w) => w.n === this._giftTierOf(i) && w.variant === String(i.variant_id));
        if (!t || seen[t.n]) {
          plan.updates[i.key] = 0;
        } else {
          seen[t.n] = true;
          if (i.quantity !== 1) plan.updates[i.key] = 1;
        }
      });
      wanted.forEach((t) => { if (!seen[t.n]) plan.add.push(t); });
      return plan;
    }

    _giftHasWork(plan) {
      return Object.keys(plan.updates).length > 0 || plan.add.length > 0;
    }

    _giftPending() {
      return !!(this._giftTimer || this._giftBusy || this._giftOpening);
    }

    /* Appele au debut de chaque _render(). Aucun appel reseau s'il n'y a rien a changer. */
    _queueGiftSync() {
      if (!this.cart) return;
      if (!this.gifts && !this._hasGiftLines()) {
        /* promo eteinte et plus aucun cadeau : rien a faire ; on rend seulement le bouton de
           commande s'il venait d'etre mis en attente par le retrait des cadeaux restants */
        if (!this._giftBusy) this._setCheckoutBusy(false);
        return;
      }
      this._giftOpening = false;
      if (this._giftStopped) { this._setCheckoutBusy(false); return; }
      this._giftCheckPrices();
      if (!this._giftHasWork(this._giftPlan())) {
        this._giftLastPlan = null;   /* etat stable atteint */
        this._giftRepeat = 0;
        if (!this._giftBusy) this._setCheckoutBusy(false);
        return;
      }
      this._setCheckoutBusy(true);
      if (this._giftBusy || this._giftTimer) return;
      /* Au chargement d'une page, d'autres scripts ecrivent dans le panier (langue du navigateur,
         applis) : deux ecritures simultanees peuvent s'ecraser. La toute premiere passe attend donc
         1,5 s, sauf si le panier est deja ouvert. */
      const wait = !this._giftStarted && !this.isOpen ? 1500 : 0;
      this._giftStarted = true;
      this._giftTimer = setTimeout(() => { this._giftTimer = null; this._giftSync(); }, wait);
    }

    /* Une passe : attend la fin de l'action en cours, relit le panier, applique le plan
       (1 appel pour les retraits, 1 par cadeau a ajouter), relit, redessine. */
    async _giftSync() {
      if (this._giftBusy) return;
      this._giftBusy = true;
      let redraw = true;
      let added = false;
      try {
        for (let i = 0; this.isUpdating && i < 100; i++) await this._giftSleep(50);
        if (this.isUpdating) { redraw = false; return; }   /* l'action en cours redessinera et relancera */
        this.isUpdating = true;
        try {
          await this._giftFetch();
          this._giftCheckPrices();
          let plan = this._giftPlan();
          if (!this._giftHasWork(plan)) return;
          /* Garde-fous. Le meme plan qui revient = l'ecriture precedente n'a rien change (deux
             ecritures simultanees dans le panier peuvent s'ecraser) : on reessaie une fois apres
             une courte pause ; s'il revient encore, on abandonne ce palier. Plafond de passes. */
          const signOf = (p) => JSON.stringify([p.updates, p.add.map((t) => t.n + ':' + t.variant)]);
          let sign = signOf(plan);
          if (sign === this._giftLastPlan) {
            this._giftRepeat = (this._giftRepeat || 0) + 1;
            if (this._giftRepeat >= 2) {
              plan.add.forEach((t) => this._giftSetKo(t, 'stock'));
              if (Object.keys(plan.updates).length) this._giftStop('le panier ne prend pas la correction');
              return;
            }
            await this._giftSleep(900);
            await this._giftFetch();
            this._giftCheckPrices();
            plan = this._giftPlan();
            if (!this._giftHasWork(plan)) return;
            sign = signOf(plan);
          } else {
            this._giftRepeat = 0;
          }
          this._giftPasses = (this._giftPasses || 0) + 1;
          if (this._giftPasses > 40) { this._giftStop('trop de passes'); return; }
          this._giftLastPlan = sign;
          if (Object.keys(plan.updates).length) {
            const r = await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ updates: plan.updates }),
            });
            if (!r.ok) throw new Error('update ' + r.status);
            this.cart = await r.json();
          }
          for (const t of plan.add) added = (await this._giftAdd(t)) || added;
          /* relit aussi après un retrait : la réponse de update.js peut arriver sans le code promo (28/10) */
          await this._giftFetch();
          this._giftErrors = 0;
        } finally {
          this.isUpdating = false;
        }
      } catch (e) {
        /* panne reseau : une seule nouvelle tentative, puis on laisse le panier tranquille */
        this._giftErrors = (this._giftErrors || 0) + 1;
        this._giftLastPlan = null;
        if (this._giftErrors >= 2) this._giftStop('erreur réseau');
      } finally {
        this._giftBusy = false;
        if (redraw) {
          this._syncInsuranceToggle();
          /* un cadeau vient d'arriver en bas de liste : on l'amene a l'ecran */
          this._render().then(() => {
            if (!added || !this.isOpen || !this.itemsEl) return;
            const lines = this.itemsEl.querySelectorAll('.dev-cart__item--gift');
            if (lines.length) lines[lines.length - 1].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          });
        }
      }
    }

    async _giftFetch() {
      const r = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!r.ok) throw new Error('cart ' + r.status);
      this._giftClock(r);
      this.cart = await r.json();
    }

    /* Ajout d'un cadeau, quantite 1. Refus (rupture, variante inconnue, 429…) : le palier est
       marque indisponible et on ne reessaie pas. */
    async _giftAdd(t) {
      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(t.variant), quantity: 1, properties: { _gh_cadeau: t.n } }] }),
        });
        if (r.ok) return true;
      } catch (e) { /* traite comme un refus */ }
      this._giftSetKo(t, 'stock');
      return false;
    }

    _giftStop(reason) {
      this._giftStopped = reason;
      this._setCheckoutBusy(false);
      console.warn('[panier] cadeaux : synchronisation arrêtée (' + reason + ')');
    }

    _giftSleep(ms) {
      return new Promise((res) => setTimeout(res, ms));
    }

    /* Bouton « Commander » : inerte tant que les cadeaux ne sont pas a jour. */
    _setCheckoutBusy(on) {
      if (!!this._giftCtaBusy === !!on) return;
      this._giftCtaBusy = !!on;
      const a = document.getElementById('dev-checkout-cta');
      if (!a) return;
      a.classList.toggle('is-syncing', !!on);
      if (on) a.setAttribute('aria-disabled', 'true'); else a.removeAttribute('aria-disabled');
    }

    /* Attend la fin de la synchronisation ; au bout de `max` ms on laisse partir (le paiement
       n'est jamais bloque). */
    _whenGiftsSettled(max) {
      return new Promise((res) => {
        const t0 = Date.now();
        const tick = () => {
          if (!this._giftPending() || Date.now() - t0 > max) res(); else setTimeout(tick, 60);
        };
        tick();
      });
    }

    _giftLeave(e) {
      if (!this._giftPending()) return;          /* rien en cours : comportement normal */
      e.preventDefault();
      if (this._giftLeaving) return;
      this._giftLeaving = true;
      this._whenGiftsSettled(8000).then(() => {
        this._giftLeaving = false;
        const a = document.getElementById('dev-checkout-cta');
        window.location.href = a ? a.href : '/checkout';
      });
    }

    /* Compteur de l'en-tete : les cadeaux ne comptent pas (meme regle que sections/header.liquid). */
    _giftHeaderCount() {
      if (!this.cart || (!this.gifts && !this._hasGiftLines())) return;
      const n = (this.cart.item_count || 0)
        - this.cart.items.reduce((s, i) => s + (this._isGift(i) ? i.quantity : 0), 0);
      document.querySelectorAll('.header__cart-count').forEach((el) => {
        el.textContent = n;
        el.style.display = n > 0 ? '' : 'none';
      });
    }

    _moneyShort(cents) {
      return this._money(cents).replace(/[.,]00(?!\d)/, '');
    }

    _giftIcon(name) {
      const p = {
        gift: '<path d="M2 6h12v3H2zM3 9h10v5H3zM8 6v8M8 6C6.5 6 4.5 5.4 4.5 3.9 4.5 2.6 6.6 2.2 8 6zm0 0c1.5 0 3.5-.6 3.5-2.1C11.5 2.6 9.4 2.2 8 6z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round" fill="none"/>',
        truck: '<path d="M1.5 4h8v7h-8zM9.5 6.5h3l2 2.5v2h-5z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round" fill="none"/><circle cx="4.5" cy="11.8" r="1.4" fill="currentColor"/><circle cx="11.8" cy="11.8" r="1.4" fill="currentColor"/>',
        check: '<path d="M3 8.4l3.2 3.1L13 4.8" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>',
        cross: '<path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/>',
      }[name];
      return '<svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">' + p + '</svg>';
    }

    /* Reperes de la barre : livraison offerte (table par pays) + paliers cadeaux, dans l'ordre
       des seuils ; deux recompenses au meme seuil partagent le meme repere. */
    _giftMilestones() {
      const list = [];
      const push = (cents, reward) => {
        let m = list.find((x) => x.cents === cents);
        if (!m) { m = { cents, rewards: [] }; list.push(m); }
        m.rewards.push(reward);
      };
      const T = this.gifts.t;
      const two = this.freeShippingHomeCents > this.freeShippingCents;
      /* les cadeaux d'abord : a seuil egal, le cadeau est nomme avant la livraison */
      this.gifts.tiers.forEach((t) => {
        const st = this._giftState(t);
        push(t.cents, { type: 'gift', tier: t, ko: st === 'stock' || st === 'prix', mark: t.label, text: t.label });
      });
      if (this.freeShippingCents > 0) {
        push(this.freeShippingCents, two
          ? { type: 'ship', mark: T.repere_relais, text: T.livraison_relais }
          : { type: 'ship', mark: T.repere_livraison, text: T.livraison });
      }
      if (two) push(this.freeShippingHomeCents, { type: 'ship', mark: T.repere_domicile, text: T.livraison_domicile });
      return list.sort((a, b) => a.cents - b.cents);
    }

    _renderTiers() {
      const box = this.shippingMsg.parentNode;
      const T = this.gifts.t;
      /* 2026-10-28 (lot 1, B2) : deux bases, comme Shopify au paiement : cadeaux sur _paidSubtotal() (avant
         les remises de commande), livraison offerte sur _shipSubtotal() (apres). Voir _shipSubtotal(). */
      const sub = this._paidSubtotal();
      const subShip = this._shipSubtotal();
      const baseOf = (r) => (r.type === 'ship' ? subShip : sub);
      const ms = this._giftMilestones();
      const n = ms.length;
      const live = (m) => m.rewards.filter((r) => !r.ko);
      const todo = (m) => live(m).filter((r) => baseOf(r) < m.cents);
      const reached = (m) => m.rewards.every((r) => baseOf(r) >= m.cents);

      /* message : prochain repere qui offre encore quelque chose */
      const next = ms.find((m) => todo(m).length);
      let msg;
      if (next) {
        const gifts = todo(next).filter((r) => r.type === 'gift').map((r) => this._esc(r.text)).join(' + ');
        const ship = todo(next).filter((r) => r.type === 'ship').map((r) => this._esc(r.text)).join(' + ');
        const reste = Math.max.apply(null, todo(next).map((r) => next.cents - baseOf(r)));
        const tpl = gifts && ship ? T.prochain_les_deux : (gifts ? T.prochain_cadeau : T.prochain_livraison);
        msg = '<em>' + this._esc(tpl || '')
          .replace('__AMOUNT__', '<strong>' + this._money(reste) + '</strong>')
          .replace('__GIFT__', '<strong>' + gifts + '</strong>')
          .replace('__SHIPPING__', ship) + '</em>';
      } else {
        /* tout est atteint ; si plus aucun cadeau n'est disponible, on n'annonce que la livraison */
        const anyGift = ms.some((m) => live(m).some((r) => r.type === 'gift'));
        msg = '<em>' + this._esc(anyGift ? (T.tout_debloque || '') : this.i18n.shippingFree) + '</em> 🎉';
      }
      this.shippingMsg.innerHTML = msg;

      /* remplissage : reperes a espacement egal, proportionnel entre deux reperes */
      let pct = 100;
      for (let i = 0; i < n; i++) {
        if (!reached(ms[i])) {
          const from = i ? ms[i - 1].cents : 0;
          const pos = (k) => (k < 0 ? 0 : (k + 0.5) / (n - 0.5));
          const b = Math.min.apply(null, ms[i].rewards.map(baseOf));
          const part = Math.min(1, Math.max(0, (b - from) / (ms[i].cents - from)));
          pct = (pos(i - 1) + part * (pos(i) - pos(i - 1))) * 100;
          break;
        }
      }

      const cols = ms.map((m) => {
        const done = reached(m);
        const allKo = !live(m).length;
        const hasGift = m.rewards.some((r) => r.type === 'gift');
        const icon = allKo ? 'cross' : (done ? 'check' : (hasGift ? 'gift' : 'truck'));
        const label = m.rewards.map((r) => this._esc(r.ko ? T.indisponible : r.mark)).join(' + ');
        return '<li class="dev-cart__tier' + (done && !allKo ? ' is-done' : '') + (allKo ? ' is-ko' : '') + (m === next ? ' is-next' : '') + '">' +
          '<span class="dev-cart__tier-dot">' + this._giftIcon(icon) + '</span>' +
          '<span class="dev-cart__tier-amount">' + this._moneyShort(m.cents) + '</span>' +
          '<span class="dev-cart__tier-label">' + label + '</span>' +
        '</li>';
      }).join('');

      /* cadeau deja gagne mais indisponible : on le dit, sans rien bloquer */
      const lost = [];
      ms.forEach((m) => { m.rewards.forEach((r) => { if (r.ko && baseOf(r) >= m.cents) lost.push(this._esc(r.text)); }); });
      const note = lost.length
        ? '<p class="dev-cart__tiers-note">' + this._esc(T.note_indisponible || '').replace('__GIFT__', lost.join(' + ')) + '</p>'
        : '';

      box.classList.add('dev-cart__shipping--tiers');
      let el = document.getElementById('dev-cart-tiers');
      if (!el) {
        el = document.createElement('div');
        el.id = 'dev-cart-tiers';
        el.className = 'dev-cart__tiers';
        box.appendChild(el);
      }
      const before = typeof this._giftPct === 'number' ? this._giftPct : 0;
      el.style.setProperty('--gh-paliers', n);
      /* compte a rebours de la promo (reglage du theme) : emplacement rempli par assets/gh-rebours.js */
      if (this._giftCountdown === undefined) {
        this._giftCountdown = false;
        try {
          const rb = document.getElementById('gh-rebours-config');
          if (rb) this._giftCountdown = !!JSON.parse(rb.textContent).emplacements.panier;
        } catch (e) { /* pas de compte a rebours */ }
      }
      const countdown = this._giftCountdown ? '<p class="dev-cart__tiers-end" data-gh-rebours="panier" hidden></p>' : '';
      el.innerHTML =
        '<div class="dev-cart__tiers-track"><div class="dev-cart__tiers-fill" style="width:' + before + '%"></div></div>' +
        '<ol class="dev-cart__tiers-list">' + cols + '</ol>' + note + countdown;
      this._giftPct = pct;
      const fill = el.querySelector('.dev-cart__tiers-fill');
      requestAnimationFrame(() => { if (fill) fill.style.width = pct + '%'; });
      if (countdown && window.ghRebours) window.ghRebours.maj();
    }

    _clearTiers() {
      const el = document.getElementById('dev-cart-tiers');
      if (!el) return;
      el.parentNode.classList.remove('dev-cart__shipping--tiers');
      el.remove();
      this._giftPct = 0;
    }

    /* Ligne cadeau : etiquette, pas de quantite. « Offert » seulement si le prix renvoye par
       /cart.js est 0 ; sinon le vrai prix, avec un lien pour le retirer (il ne revient pas). */
    _giftItemHTML(item) {
      const T = this.gifts ? this.gifts.t : {};
      const imgSrc = item.featured_image?.url
        ? this._imgSrc(item.featured_image.url, '176x176')
        : item.image ? this._imgSrc(item.image, '176x176') : '';
      const variantLabel =
        item.variant_title && item.variant_title !== 'Default Title'
          ? '<p class="dev-cart__item-variant">' + this._esc(item.variant_title) + '</p>'
          : '';
      const tier = this.gifts ? this.gifts.tiers.find((t) => t.n === this._giftTierOf(item)) : null;
      const from = tier && T.des ? ' · ' + this._esc(T.des).replace('__AMOUNT__', this._moneyShort(tier.cents)) : '';
      const price = this._linePrice(item);
      const orig = typeof item.original_line_price === 'number' ? item.original_line_price : price;
      const compare = orig > price ? '<span class="dev-cart__item-compare">' + this._money(orig) + '</span>' : '';
      const right = price <= 0
        ? '<div class="dev-cart__item-prices">' + compare +
            '<span class="dev-cart__item-price dev-cart__gift-free">' + this._esc(T.offert || '') + '</span></div>'
        : '<div class="dev-cart__item-prices">' + compare +
            '<span class="dev-cart__item-price">' + this._money(price) + '</span></div>' +
          '<button type="button" class="dev-cart__item-delete" data-dev-del="' + item.key + '" data-dev-gift-del aria-label="' +
            this._esc(this.i18n.remove) + ' ' + this._esc(item.product_title) + '">' + this._esc(this.i18n.remove) + '</button>';

      return /* html */ `
        <div class="dev-cart__item dev-cart__item--gift" data-key="${item.key}">
          <div class="dev-cart__item-img">
            ${imgSrc ? `<img src="${imgSrc}" alt="${this._esc(item.product_title)}" loading="lazy" width="88" height="88">` : ''}
          </div>
          <div class="dev-cart__item-mid">
            <p class="dev-cart__item-title" title="${this._esc(item.product_title)}">${this._esc(this._titreAffichage(item))}</p>
            ${variantLabel}
            <p class="dev-cart__gift-tag">${this._giftIcon('gift')}<span>${this._esc(T.etiquette || '')}${from}</span></p>
          </div>
          <div class="dev-cart__item-right">
            ${right}
          </div>
        </div>`;
    }

    /* ══════════════════════════════════════════
       HELPERS
    ══════════════════════════════════════════ */

    /**
     * Format cents using Shopify's money_format string.
     * Supports: {{amount}}, {{amount_no_decimals}}, {{amount_with_comma_separator}},
     *           {{amount_no_decimals_with_comma_separator}}, {{amount_with_apostrophe_separator}}
     */
    _money(cents) {
      const amount = cents / 100;

      return this.moneyFormat
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, () =>
          Math.round(amount).toLocaleString('de-CH').replace(/\./g, "'")
        )
        .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/, () =>
          amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'")
        )
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, () =>
          Math.round(amount).toLocaleString('en-US').replace(/,/g, ',')
        )
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, () =>
          amount.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
        )
        .replace(/\{\{\s*amount\s*\}\}/, () =>
          amount.toFixed(2)
        );
    }

    _imgSrc(src, size) {
      if (!src) return '';
      return src
        .replace(/^http:/, 'https:')
        .replace(/(_\d+x\d+)?(\.(?:gif|jpg|jpeg|png|webp))(\?.*)?$/i, '_' + size + '$2$3');
    }

    _esc(str) {
      return String(str ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    _setPromoMsg(msg, type) {
      if (!this.promoMsg) return;
      this.promoMsg.textContent = msg;
      this.promoMsg.className   = 'dev-cart__promo-msg' + (type ? ' ' + type : '');
    }

    _setDiscountCookie(code) {
      if (!code) return;
      const maxAge = 30 * 24 * 60 * 60;
      document.cookie = 'discount_code=' + encodeURIComponent(code) + '; path=/; max-age=' + maxAge + '; SameSite=Lax';
    }

    _clearDiscountCookie() {
      document.cookie = 'discount_code=; path=/; max-age=0';
    }
  }

  /* ── Boot ── */
  function boot() {
    if (!window.developlyCart) {
      window.developlyCart = new DeveloplyCart();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
